/* ───────────────────────────────────────────────────────────────────────────
   Joe's Kitchen — Catering page (v2)

   Wires up the variant-selection modal, sold-out / price-delta logic,
   floating cart bar, and Shopify Cart API.
   --------------------------------------------------------------------------- */
(function () {
  'use strict';

  const SERVINGS_HINTS_FALLBACK = {
    'Half Pan': 'Serves 8–10',
    'Full Pan': 'Serves 16–20',
  };

  const moneyFormat = (window.JK_CATERING_MONEY_FORMAT || '${{amount}}');

  // ------------------------------------------------------------------
  // Money formatting
  // ------------------------------------------------------------------
  function formatMoneyCents(cents) {
    if (cents == null || isNaN(cents)) return '';
    const amount = (cents / 100).toFixed(2);
    return moneyFormat
      .replace(/\{\{\s*amount\s*\}\}/, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','));
  }

  // ------------------------------------------------------------------
  // Per-card state
  // ------------------------------------------------------------------
  const cardState = new Map(); // cardEl -> { product, selections, qty }

  function parseProduct(cardEl) {
    const dataEl = cardEl.querySelector('[data-product-json]');
    if (!dataEl) return null;
    try { return JSON.parse(dataEl.textContent); }
    catch (e) { console.error('[catering] bad product JSON', e); return null; }
  }

  function getState(cardEl) {
    let s = cardState.get(cardEl);
    if (!s) {
      s = { product: parseProduct(cardEl), selections: {}, qty: 1 };
      cardState.set(cardEl, s);
    }
    return s;
  }

  // ------------------------------------------------------------------
  // Variant resolution
  // ------------------------------------------------------------------
  function variantsMatchingPartial(product, partial) {
    return product.variants.filter(v => {
      return product.options.every((opt, i) => {
        const sel = partial[opt.name];
        if (!sel) return true;
        return v.options[i] === sel;
      });
    });
  }

  function findExactVariant(product, selections) {
    const ordered = product.options.map(o => selections[o.name]);
    if (ordered.some(v => v == null)) return null;
    return product.variants.find(v =>
      v.options.every((val, i) => val === ordered[i])
    ) || null;
  }

  function getMinPriceCents(product) {
    return Math.min.apply(null, product.variants.map(v => Number(v.price)));
  }

  // ------------------------------------------------------------------
  // Modal open/close
  // ------------------------------------------------------------------
  function closeAllModals(except) {
    document.querySelectorAll('.jk-cat .product-card').forEach(card => {
      if (card === except) return;
      card.classList.remove('modal-open');
      const m = card.querySelector('.card-modal');
      if (m) m.classList.remove('active');
    });
  }

  function openModal(cardEl) {
    closeAllModals(cardEl);
    const state = getState(cardEl);
    if (!state.product) return;

    state.selections = {};
    state.qty = 1;

    const modal = cardEl.querySelector('.card-modal');
    if (!modal) return;

    cardEl.querySelectorAll('.option-pill').forEach(p => p.classList.remove('active'));
    cardEl.querySelectorAll('.servings').forEach(s => s.textContent = '');
    const qtyNum = cardEl.querySelector('[data-qty-num]');
    if (qtyNum) qtyNum.textContent = '1';

    refreshSoldOutAndDeltas(cardEl);
    refreshPriceAndAddBtn(cardEl);
    refreshStepperState(cardEl);

    const removeBtn = cardEl.querySelector('[data-remove-btn]');
    if (removeBtn) {
      removeBtn.style.display = cardEl.classList.contains('added') ? '' : 'none';
    }

    // Populate current-order section (async — renders after modal is already visible)
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(r => r.json())
      .then(cart => renderCurrentOrder(cardEl, cart))
      .catch(() => {});

    modal.classList.add('active');
    cardEl.classList.add('modal-open');
  }

  function closeModal(cardEl) {
    const modal = cardEl.querySelector('.card-modal');
    if (modal) modal.classList.remove('active');
    cardEl.classList.remove('modal-open');
  }

  // ------------------------------------------------------------------
  // Selection / price / delta updates
  // ------------------------------------------------------------------
  function selectOption(cardEl, optName, value) {
    const state = getState(cardEl);
    if (!state.product) return;

    state.selections[optName] = value;

    // Toggle pill active state in this group
    const group = cardEl.querySelector(`[data-option-group="${cssEscape(optName)}"]`);
    if (group) {
      group.querySelectorAll('.option-pill').forEach(p => p.classList.remove('active'));
      const pill = group.querySelector(`.option-pill[data-option-value="${cssEscape(value)}"]`);
      if (pill) pill.classList.add('active');
    }

    // Update servings hint if Size option
    if (optName === 'Size') {
      const hintEl = cardEl.querySelector('[data-servings-for="Size"]');
      if (hintEl) hintEl.textContent = SERVINGS_HINTS_FALLBACK[value] || '';
    }

    refreshSoldOutAndDeltas(cardEl);
    refreshPriceAndAddBtn(cardEl);
  }

  function refreshSoldOutAndDeltas(cardEl) {
    const state = getState(cardEl);
    if (!state.product) return;
    const { product, selections } = state;

    product.options.forEach(opt => {
      const group = cardEl.querySelector(`[data-option-group="${cssEscape(opt.name)}"]`);
      if (!group) return;

      group.querySelectorAll('.option-pill').forEach(pill => {
        const value = pill.getAttribute('data-option-value');
        const partial = Object.assign({}, selections, { [opt.name]: value });
        const matches = variantsMatchingPartial(product, partial);
        const anyAvail = matches.some(v => v.available);
        pill.classList.toggle('sold-out', !anyAvail);

        const cheapestForValue = matches
          .filter(v => v.available)
          .reduce((min, v) => Math.min(min, Number(v.price)), Infinity);
        const allMatches = variantsMatchingPartial(product, Object.assign({}, selections));
        const cheapestOverall = allMatches
          .filter(v => v.available)
          .reduce((min, v) => Math.min(min, Number(v.price)), Infinity);

        const deltaEl = pill.querySelector('.price-delta');
        if (deltaEl) {
          const delta = cheapestForValue - cheapestOverall;
          if (isFinite(delta) && delta > 0 && !pill.classList.contains('active')) {
            deltaEl.textContent = '+' + formatMoneyCents(delta);
            deltaEl.style.display = '';
          } else {
            deltaEl.textContent = '';
            deltaEl.style.display = 'none';
          }
        }
      });
    });
  }

  function refreshPriceAndAddBtn(cardEl) {
    const state = getState(cardEl);
    if (!state.product) return;
    const { product, selections, qty } = state;

    const allChosen = product.options.every(o => selections[o.name]);
    const variant = allChosen ? findExactVariant(product, selections) : null;

    const priceEl = cardEl.querySelector('[data-modal-price]');
    if (priceEl) {
      if (variant) {
        priceEl.textContent = formatMoneyCents(Number(variant.price) * qty);
      } else {
        priceEl.textContent = 'From ' + formatMoneyCents(getMinPriceCents(product));
      }
    }

    const addBtn = cardEl.querySelector('[data-add-btn]');
    if (addBtn) {
      const canAdd = !!(variant && variant.available);
      addBtn.classList.toggle('is-disabled', !canAdd);
      addBtn.disabled = !canAdd;
    }
  }

  function refreshStepperState(cardEl) {
    const state = getState(cardEl);
    const dec = cardEl.querySelector('[data-qty-dec]');
    const inc = cardEl.querySelector('[data-qty-inc]');
    if (dec) dec.classList.toggle('is-disabled', state.qty <= 1);
    if (inc) inc.classList.toggle('is-disabled', state.qty >= 50);
  }

  function changeQty(cardEl, delta) {
    const state = getState(cardEl);
    state.qty = Math.max(1, Math.min(state.qty + delta, 50));
    const num = cardEl.querySelector('[data-qty-num]');
    if (num) num.textContent = String(state.qty);
    refreshStepperState(cardEl);
    refreshPriceAndAddBtn(cardEl);
  }

  // ------------------------------------------------------------------
  // Add-to-cart
  // ------------------------------------------------------------------
  function addToCart(cardEl) {
    const state = getState(cardEl);
    if (!state.product) return;
    const variant = findExactVariant(state.product, state.selections);
    if (!variant || !variant.available) return;

    const addBtn = cardEl.querySelector('[data-add-btn]');
    if (addBtn) addBtn.classList.add('is-disabled');

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: variant.id, quantity: state.qty }] }),
    })
      .then(r => r.json())
      .then(() => refreshCart())
      .then(() => {
        cardEl.classList.add('added');
        closeModal(cardEl);
        publishCartUpdate();
        refreshHeaderCartBubble();
      })
      .catch(err => {
        console.error('[catering] add-to-cart failed', err);
        if (addBtn) addBtn.classList.remove('is-disabled');
      });
  }

  // ------------------------------------------------------------------
  // Global hook exposed for inline onclick — lets btn-decrement buttons
  // fire even inside -webkit-overflow-scrolling:touch containers on iOS.
  // ------------------------------------------------------------------
  window._jkCat = window._jkCat || {};
  window._jkCat.decrement = function (variantId, currentQty, btnEl) {
    if (btnEl.disabled) return;
    btnEl.disabled = true;
    var cardEl = btnEl.closest('.product-card');
    if (!cardEl) { btnEl.disabled = false; return; }
    decrementVariant(cardEl, variantId, currentQty, btnEl);
  };

    // ------------------------------------------------------------------
  // Current-order section — builds DOM nodes directly so click
  // listeners are attached at creation time (avoids delegation issues
  // inside overflow-y:auto scroll containers on iOS Safari).
  // ------------------------------------------------------------------
  function renderCurrentOrder(cardEl, cart) {
    const section = cardEl.querySelector('[data-current-order]');
    if (!section) return;
    const state = getState(cardEl);
    if (!state.product) return;

    const items = (cart.items || []).filter(it => it.product_id === state.product.id);

    // Clear and bail early if nothing in cart for this product
    section.innerHTML = '';
    if (items.length === 0) return;

    const labelEl = document.createElement('div');
    labelEl.className = 'current-order-label';
    labelEl.textContent = 'In your order';
    section.appendChild(labelEl);

    items.forEach(function (item) {
      const variantTitle =
        item.variant_title && item.variant_title !== 'Default Title'
          ? item.variant_title
          : (item.product_title || item.title);

      const row = document.createElement('div');
      row.className = 'current-order-item';

      const nameEl = document.createElement('span');
      nameEl.className = 'current-order-item-name';
      nameEl.textContent = variantTitle;

      const qtyEl = document.createElement('span');
      qtyEl.className = 'current-order-item-qty';
      qtyEl.textContent = '× ' + item.quantity; // × qty

      const decBtn = document.createElement('button');
      decBtn.type = 'button';
      decBtn.className = 'btn-decrement';
      decBtn.setAttribute('aria-label', 'Remove one ' + variantTitle);
      decBtn.textContent = '−';
      // Inline onclick fires directly — bypasses iOS Safari touch-scroll
      // event swallowing inside overflow-y:auto containers.
      decBtn.setAttribute('onclick',
        'event.stopPropagation();window._jkCat.decrement(' +
        item.variant_id + ',' + item.quantity + ',this)'
      );

      row.appendChild(nameEl);
      row.appendChild(qtyEl);
      row.appendChild(decBtn);
      section.appendChild(row);
    });
  }

  // ------------------------------------------------------------------
  // Decrement one unit of a specific variant (remove if qty reaches 0)
  // ------------------------------------------------------------------
  function decrementVariant(cardEl, variantId, currentQty, btnEl) {
    const newQty = Math.max(0, currentQty - 1);

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: newQty }),
    })
      .then(r => r.json())
      .then(() => refreshCart())
      .then(function (cart) {
        renderCurrentOrder(cardEl, cart); // rebuilds buttons with fresh listeners
        const stillHas = (cart.items || []).some(
          it => it.product_id === getState(cardEl).product.id
        );
        if (!stillHas) closeModal(cardEl);
        publishCartUpdate();
        refreshHeaderCartBubble();
      })
      .catch(function (err) {
        console.error('[catering] decrement failed', err);
        if (btnEl) btnEl.disabled = false;
      });
  }

  // ------------------------------------------------------------------
  // Remove from cart (zero out all variants of this product)
  // ------------------------------------------------------------------
  function removeFromCart(cardEl) {
    const state = getState(cardEl);
    if (!state.product) return;

    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(r => r.json())
      .then(cart => {
        const updates = {};
        (cart.items || []).forEach(item => {
          if (item.product_id === state.product.id) {
            updates[item.variant_id] = 0;
          }
        });
        if (!Object.keys(updates).length) return Promise.resolve(null);
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates }),
        });
      })
      .then(() => refreshCart())
      .then(() => {
        closeModal(cardEl);
        publishCartUpdate();
        refreshHeaderCartBubble();
      })
      .catch(err => console.error('[catering] remove failed', err));
  }

  // ------------------------------------------------------------------
  // Cart state
  // ------------------------------------------------------------------
  function refreshCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(r => r.json())
      .then(cart => {
        updateCardBadges(cart);
        updateCartBar(cart);
        return cart;
      });
  }

  function updateCardBadges(cart) {
    // Refresh current-order section in any open modal
    document.querySelectorAll('.jk-cat .product-card.modal-open').forEach(card => {
      renderCurrentOrder(card, cart);
    });

    document.querySelectorAll('.jk-cat .product-card').forEach(card => {
      const state = getState(card);
      if (!state.product) return;
      const productId = state.product.id;
      const totalQty = (cart.items || [])
        .filter(it => it.product_id === productId)
        .reduce((sum, it) => sum + it.quantity, 0);

      const wrap = card.querySelector('.card-image-wrap');
      let badge = card.querySelector('.card-qty-badge');
      if (totalQty > 0) {
        if (!badge && wrap) {
          badge = document.createElement('div');
          badge.className = 'card-qty-badge';
          wrap.appendChild(badge);
        }
        if (badge) {
          badge.innerHTML =
            '<svg class="check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><path d="M5 12l5 5L19 7"/></svg> ' +
            totalQty +
            '<span class="badge-sep"></span>' +
            '<button type="button" class="badge-remove-btn" data-badge-remove aria-label="Remove from order">×</button>';
        }
        card.classList.add('added');
      } else {
        if (badge) badge.remove();
        card.classList.remove('added');
      }
    });
  }

  function updateCartBar(cart) {
    const bar = document.querySelector('.jk-cat-bar');
    if (!bar) return;
    const itemCount = cart.item_count || 0;
    const totalPrice = cart.total_price || 0;

    const textEl = bar.querySelector('[data-cart-bar-text]');
    const priceEl = bar.querySelector('[data-cart-bar-price]');

    if (itemCount > 0) {
      bar.classList.add('visible');
      if (textEl) {
        textEl.innerHTML = '<strong>' + itemCount + '</strong> item' + (itemCount === 1 ? '' : 's') + ' in order';
      }
      if (priceEl) priceEl.textContent = formatMoneyCents(totalPrice);
    } else {
      bar.classList.remove('visible');
    }
  }

  function publishCartUpdate() {
    try {
      if (window.publish && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'catering-page' });
      }
    } catch (e) { /* ignore */ }
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  }

  function refreshHeaderCartBubble() {
    fetch('/?section_id=cart-icon-bubble', { headers: { 'Accept': 'text/html' } })
      .then(r => r.text())
      .then(html => {
        const newRoot = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector('.shopify-section');
        const target = document.getElementById('cart-icon-bubble');
        if (!target || !newRoot) return;
        const newCount = newRoot.querySelector('.cart-count-bubble');
        const currentCount = target.querySelector('.cart-count-bubble');
        if (newCount && currentCount) {
          currentCount.innerHTML = newCount.innerHTML;
        } else if (newCount && !currentCount) {
          target.appendChild(newCount.cloneNode(true));
        } else if (!newCount && currentCount) {
          currentCount.remove();
        }
      })
      .catch(() => {});
  }

  // ------------------------------------------------------------------
  // Filter / sort dropdowns
  // ------------------------------------------------------------------
  function toggleFilterMenu(btn) {
    const wrap = btn.closest('.filter-dropdown');
    if (!wrap) return;
    const menu = wrap.querySelector('.filter-menu');
    if (!menu) return;
    const wasOpen = menu.classList.contains('open');
    document.querySelectorAll('.jk-cat .filter-menu.open').forEach(m => m.classList.remove('open'));
    if (!wasOpen) menu.classList.add('open');
  }

  // ------------------------------------------------------------------
  // Helpers
  // ------------------------------------------------------------------
  function cssEscape(str) {
    return String(str).replace(/["\\]/g, '\\$&');
  }

  // ------------------------------------------------------------------
  // Event delegation
  // ------------------------------------------------------------------
  document.addEventListener('click', function (e) {
    const filterBtn = e.target.closest('.jk-cat .filter-btn');
    if (filterBtn) {
      e.stopPropagation();
      toggleFilterMenu(filterBtn);
      return;
    }
    if (!e.target.closest('.filter-dropdown')) {
      document.querySelectorAll('.jk-cat .filter-menu.open').forEach(m => m.classList.remove('open'));
    }

    const card = e.target.closest('.jk-cat .product-card');
    if (!card) return;

    if (e.target.closest('[data-close-modal]')) {
      e.stopPropagation();
      closeModal(card);
      return;
    }
    if (e.target.closest('[data-choose-btn]')) {
      e.stopPropagation();
      openModal(card);
      return;
    }
    const pill = e.target.closest('.option-pill');
    if (pill) {
      e.stopPropagation();
      if (pill.classList.contains('sold-out')) return;
      const optName = pill.getAttribute('data-option-name');
      const value = pill.getAttribute('data-option-value');
      if (optName && value != null) selectOption(card, optName, value);
      return;
    }
    if (e.target.closest('[data-qty-dec]')) {
      e.stopPropagation();
      const dec = e.target.closest('[data-qty-dec]');
      if (!dec.classList.contains('is-disabled')) changeQty(card, -1);
      return;
    }
    if (e.target.closest('[data-qty-inc]')) {
      e.stopPropagation();
      const inc = e.target.closest('[data-qty-inc]');
      if (!inc.classList.contains('is-disabled')) changeQty(card, 1);
      return;
    }
    if (e.target.closest('[data-add-btn]')) {
      e.stopPropagation();
      const btn = e.target.closest('[data-add-btn]');
      if (!btn.classList.contains('is-disabled')) addToCart(card);
      return;
    }
    if (e.target.closest('[data-remove-btn]')) {
      e.stopPropagation();
      removeFromCart(card);
      return;
    }
    if (e.target.closest('[data-badge-remove]')) {
      e.stopPropagation();
      removeFromCart(card);
      return;
    }
    // Clicking the badge body (not the × button) opens the modal to manage the order
    const badge = e.target.closest('.card-qty-badge');
    if (badge && !e.target.closest('[data-badge-remove]')) {
      e.stopPropagation();
      openModal(card);
      return;
    }
    // [data-cart-decrement] buttons use direct listeners (renderCurrentOrder)
    // — no delegation case needed here.

    // Click outside modal but inside card -> close any open modal on this card
    const openModalEl = card.querySelector('.card-modal.active');
    if (openModalEl && !openModalEl.contains(e.target) && !e.target.closest('[data-choose-btn]')) {
      closeModal(card);
    }
  });

  // Sync from external cart updates
  function subscribeCartUpdates() {
    document.addEventListener('cart:refresh', () => refreshCart().catch(() => {}));
    try {
      if (window.subscribe && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
        window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, () => refreshCart().catch(() => {}));
      }
    } catch (e) { /* ignore */ }
  }

  // ------------------------------------------------------------------
  // Init
  // ------------------------------------------------------------------
  function init() {
    if (!document.querySelector('.jk-cat')) return;
    subscribeCartUpdates();
    refreshCart().catch(() => {});
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
